import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { ListItemButton } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import SubMenu, { MenuItemData } from '../../common/SubMenu';
import NavSubMenu from './NavSubMenu';
import {
    buttonHoverStyling,
    excludedDefinitionIds,
    Industrial_Sensors_Products_ID,
} from './Product';
import { useNavigationNodes } from '../../../graphql/pimcore/modules/siteNavigation/contexts/NavigationNodesProvider';
import { NavigationNode } from '../../../graphql/pimcore/modules/siteNavigation/types/NavigationNode';
import { useDefinitions } from '../../../contexts/DefinitionsContext';
import type { ProductGroupById } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { useProductGroups } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import { useLocale } from '../../../contexts/LocaleProvider';
import type { DmLangCode } from '../../../utils/Locale';

interface DynamicMainMenuListProps {
    buttonStyling: React.CSSProperties;
    isNavSubMenu?: boolean;
    handleNavClose?: () => void;
    renderAfter?: (node: NavigationNode) => React.ReactNode;
}

export default function DynamicMainMenuList({
    buttonStyling,
    isNavSubMenu,
    handleNavClose,
    renderAfter,
}: DynamicMainMenuListProps) {
    const { globalMenuNodes, navigationNodes } = useNavigationNodes();

    // Identify the "Home" root node to group top-level menus
    const rootNode = useMemo(
        () => navigationNodes.find((n) => n.key === 'Home'),
        [navigationNodes]
    );

    // Top-level global menu items are children of Home sorted by index property
    const topLevel = useMemo(
        () =>
            rootNode
                ? globalMenuNodes
                      .filter((n) => n.parent.id === rootNode.id)
                      .sort((a, b) => a.index - b.index)
                : [],
        [globalMenuNodes, rootNode]
    );

    return (
        <>
            {topLevel.map((node, index) => (
                <li key={node.key} role="none">
                    <DynamicMenuButton
                        index={index}
                        node={node}
                        buttonStyling={buttonStyling}
                        isNavSubMenu={isNavSubMenu}
                        handleNavClose={handleNavClose}
                    />
                    {renderAfter && renderAfter(node)}
                </li>
            ))}
        </>
    );
}

/**
 * Build submenu items in the same order as received from the GraphQL response.
 */
function buildNavItemsRec(
    parent: NavigationNode,
    globalMenuNodes: NavigationNode[],
    level = 0
): MenuItemData[] {
    const childNodes = globalMenuNodes
        .filter((n) => n.menuProperties.isGlobalMenuItem && n.parent.id === parent.id)
        .sort((a, b) => a.index - b.index);

    return childNodes.map((child) => ({
        id: child.id,
        englishTitle: child.key,
        title: child.name,
        level,
        route: child.route || undefined,
        children: level < 1 ? buildNavItemsRec(child, globalMenuNodes, level + 1) : undefined,
    }));
}

function buildDmCategoryRoute(
    definition: DetectedDefinition,
    level: number,
    categoryName: string
): string {
    if (definition.id === Industrial_Sensors_Products_ID) {
        return 'https://www.telemecaniquesensors.com';
    }
    let route = `/products/${categoryName}`;
    if (level > 0) {
        // e.g., /products/Capacitors?79=482
        route += `?79=${definition.id}`;
    }
    return route;
}

// Build definition-based submenu items for Products
function mapDmCategoryTreeToMenuItemsRec(
    definitions: DetectedDefinition[],
    productGroupById: ProductGroupById,
    dmLangCode: DmLangCode,
    level = 0,
    topDefinition: DetectedDefinition | undefined = undefined
): MenuItemData[] {
    if (level > 1) {
        // we build only 2 levels deep
        return [];
    }
    return definitions
        .filter((def) => !excludedDefinitionIds.includes(def.id))
        .map((def) => {
            let children = def.childDefinitions ?? [];

            // 若是 Sensors (id: 611)，則加上 dummy 項目並排序
            if (def.id === 611) {
                const dummy: DetectedDefinition = {
                    nameBreadcrumb: '',
                    nameBreadcrumbs: [],
                    id: Industrial_Sensors_Products_ID,
                    name: 'Industrial Sensors',
                    displayName:
                        productGroupById[Industrial_Sensors_Products_ID]?.name ||
                        'Industrial Sensors',
                    displayNames: [],
                    childDefinitions: [],
                };
                children = [...children, dummy].sort((a, b) => a.name.localeCompare(b.name));
            }

            const childTopDefinition = topDefinition ? topDefinition : def;

            return {
                id: def.id.toString(),
                englishTitle: def.displayName,
                title:
                    def.displayNames.find((d) => d.language === dmLangCode)?.display ||
                    def.displayName,
                level,
                route: buildDmCategoryRoute(def, level, childTopDefinition.name),
                children:
                    children.length > 0
                        ? mapDmCategoryTreeToMenuItemsRec(
                              children,
                              productGroupById,
                              dmLangCode,
                              level + 1,
                              childTopDefinition
                          )
                        : undefined,
            };
        });
}

interface DynamicMenuButtonProps extends DynamicMainMenuListProps {
    index: number; // index of the top menu item, begins from 0, used for submenu horizontal offsets
    node: NavigationNode;
}

function DynamicMenuButton({
    index,
    node,
    buttonStyling,
    isNavSubMenu,
    handleNavClose,
}: DynamicMenuButtonProps) {
    const navigate = useNavigate();
    const { rawDefinitions } = useDefinitions();
    const { productGroupById } = useProductGroups();
    const { globalMenuNodes } = useNavigationNodes();
    const { dmLangCode } = useLocale();
    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | (EventTarget & Element) | null>(null);

    const buildNavItems = useCallback(
        (parent: NavigationNode): MenuItemData[] => {
            return buildNavItemsRec(parent, globalMenuNodes);
        },
        [globalMenuNodes]
    );

    const mapDmCategoryTreeToMenuItems = useCallback(
        (definitions: DetectedDefinition[]): MenuItemData[] => {
            return mapDmCategoryTreeToMenuItemsRec(definitions, productGroupById, dmLangCode);
        },
        [productGroupById, dmLangCode]
    );

    const menuItems: MenuItemData[] = useMemo(() => {
        // Base items from Pimcore (in server-provided order)
        const navItems = buildNavItems(node);
        // Append DefinitionManager items if configured
        if (node.menuProperties.additionalMenuItemsSource === 'dmProductGroups') {
            const defItems = mapDmCategoryTreeToMenuItems(rawDefinitions || []);
            return [...navItems, ...defItems];
        }
        return navItems;
    }, [node, rawDefinitions, buildNavItems, mapDmCategoryTreeToMenuItems]);

    const handleOpen = (e: React.MouseEvent) => {
        setAnchorEl(e.currentTarget as HTMLElement);
        setIsOpen(true);
    };

    const handleClose = (item?: MenuItemData) => {
        setAnchorEl(null);
        setIsOpen(false);
        if (!item?.route) {
            handleNavClose?.();
            return;
        }
        if (item.route.startsWith('http')) {
            window.open(item.route, '_blank');
        } else {
            navigate(item.route, { state: { shouldClearAllProps: true } });
        }
        handleNavClose?.();
    };

    return (
        <>
            <ListItemButton
                sx={{
                    ...buttonStyling,
                    ...(isNavSubMenu
                        ? {}
                        : { borderBottom: isOpen ? '4px solid #e5673e' : '4px solid transparent' }),
                    ...(isNavSubMenu ? {} : buttonHoverStyling),
                }}
                onClick={handleOpen}
            >
                <FormattedMessage id={node.key} defaultMessage={node.name} />
                {isNavSubMenu && (
                    <ChevronRight
                        fontSize="small"
                        sx={{ marginLeft: 'auto', transition: 'transform 0.3s ease' }}
                    />
                )}
            </ListItemButton>
            {isNavSubMenu ? (
                <NavSubMenu
                    isMenuOpen={isOpen}
                    anchorEl={anchorEl}
                    menuItemsData={menuItems}
                    menuLevels={2}
                    onClose={handleClose}
                    setMenuOpen={setIsOpen}
                    setAnchorEl={setAnchorEl}
                    handleNavClose={handleNavClose}
                />
            ) : (
                <SubMenu
                    isMenuOpen={isOpen}
                    anchorEl={anchorEl}
                    menuItemsData={menuItems}
                    menuLevels={2}
                    onClose={handleClose}
                    mainMenuStartOffset={-24}
                    mainMenuExpandOffset={index === 0 ? -150 : -285}
                    mainMenuIndex={index}
                />
            )}
        </>
    );
}
